import { Backdrop, Button, CircularProgress, Dialog, DialogActions, DialogContent, Divider, Grid, Paper, Typography } from "@mui/material";
import { useSnackbar } from "notistack";
import React from "react";
import { ApiError } from "../../Api/ApiError";
import { getReserveDetail, getReserveDetailResult } from "../../Api/Mypage";
import { ReserveDetail } from "../../Objects/ReserveDetail";
import EquipmentAdjustmentDetail from "./EquipmentAdjustmentDetail";
import EquipmentAdjustmentTotal from "./EquipmentAdjustmentTotal";
import ReserveCancelDialog from "./ReserveCancelDialog";
import ReserveConditions from "./ReserveConditions";
import ReserveTotal from "./ReserveTotal";
import StudioAdjustmentDetail from "./StudioAdjustmentDetail";
import StudioAdjustmentTotal from "./StudioAdjustmentTotal";
import StudioItem from "./StudioItem";

export interface ReserveDetailDialogProps {
    open: boolean
    onClose: () => void
    stdrv_id?: number
}

const ReserveDetailDialog: React.FC<ReserveDetailDialogProps> = ({
    open,
    onClose,
    stdrv_id,
}) => {
    let { enqueueSnackbar } = useSnackbar()

    let [isLoading, setIsLoading] = React.useState<boolean>(false)
    let [reserveDetail, setReserveDetail] = React.useState<ReserveDetail>()

    let [dialog, setDialog] = React.useState<boolean>(false)

    let [cancelDialog, setCancelDialog] = React.useState<boolean>(false)

    const abortControllerRef = React.useRef(new AbortController())

    React.useEffect(() => {
        if (open && stdrv_id) {
            setIsLoading(true)
            setDialog(false)
            // データ取得
            getReserveDetail({
                stdrv_id: stdrv_id
            }, {signal: abortControllerRef.current.signal}).then((result: getReserveDetailResult) => {
                setReserveDetail(result.reserveDetail)
                setIsLoading(false)
                setDialog(true)
            }).catch((e: any) => {
                if( e.__CANCEL__ ) {
                    console.info("canceled")
                } else if (e instanceof ApiError) {
                    enqueueSnackbar(e.message, { variant: "error" })
                } else if (e instanceof Error) {
                    enqueueSnackbar(e.message, { variant: "error" })
                }else {
                    enqueueSnackbar("通信に失敗しました", { variant: "error" })
                }
            })
        }
    }, [open, stdrv_id, enqueueSnackbar])

    React.useEffect(() => {
        abortControllerRef.current = new AbortController()
        return () => {
            abortControllerRef.current.abort()
        }
    }, [])

    /**
     * View
     */
    return (
        <>
            <Backdrop
                sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
                open={isLoading}
            >
                <CircularProgress color="inherit" />
            </Backdrop>
            <Dialog
                onClose={() => {
                    onClose()
                    setDialog(false)
                }}
                TransitionProps={{
                    onExited: () => {
                    }
                }}
                open={dialog}
                fullWidth
            >
                <DialogContent>
                    <Typography
                        variant="body1"
                        component="p"
                        textAlign="left"
                        color="primary"
                        sx={{
                            fontWeight: "bold"
                        }}>予約条件</Typography>
                    <Paper
                        variant="outlined"
                        sx={{
                            p: 1
                        }}
                    >
                        <ReserveConditions
                            reserveData={reserveDetail}
                            align="left"
                        />
                    </Paper>
                    {reserveDetail?.reserveStudio &&
                        <>
                            <Typography
                                variant="body1"
                                component="p"
                                textAlign="left"
                                color="primary"
                                sx={{
                                    fontWeight: "bold",
                                    mt: 2,
                                }}
                            >予約スタジオ</Typography>
                            <Grid container
                                spacing={1}
                            >
                                <Grid item
                                    xs={12}
                                    sm={5}
                                >
                                    <StudioItem
                                        studioData={reserveDetail?.reserveStudio}
                                    />
                                </Grid>
                                {(reserveDetail.studioAdjustmentList && reserveDetail.studioAdjustmentList.length>0) &&
                                    <>
                                        <Grid item
                                            xs={12}
                                            sm={7}
                                        >
                                            <Paper
                                                variant="outlined"
                                                sx={{
                                                    p: 1
                                                }}
                                            >
                                                <StudioAdjustmentDetail
                                                    studioAdjustmentList={reserveDetail.studioAdjustmentList}
                                                />
                                            </Paper>
                                        </Grid>
                                        <Grid item
                                            xs={12}
                                        >
                                            <Paper
                                                variant="outlined"
                                                sx={{
                                                    p: 1
                                                }}
                                            >
                                                <StudioAdjustmentTotal
                                                    studioAdjustmentList={reserveDetail.studioAdjustmentList}
                                                />
                                            </Paper>
                                        </Grid>
                                    </>
                                }
                            </Grid>
                        </>
                    }
                    {reserveDetail &&
                        <>
                            <Typography
                                variant="body1"
                                component="p"
                                textAlign="left"
                                color="primary"
                                sx={{
                                    fontWeight: "bold",
                                    mt: 2
                                }}
                            >レンタル機材</Typography>
                            <Grid container>
                                <Grid item
                                    xs={12}
                                >
                                    <EquipmentAdjustmentDetail
                                        equipmentAdjustmentList={reserveDetail.equipmentAdjustmentList}
                                    />
                                </Grid>
                                <Grid item
                                    xs={12}
                                >
                                    <Paper
                                        variant="outlined"
                                        sx={{
                                            p: 1
                                        }}
                                    >
                                        <EquipmentAdjustmentTotal
                                            equipmentAdjustmentList={reserveDetail.equipmentAdjustmentList}
                                        />
                                    </Paper>
                                </Grid>
                            </Grid>
                        </>
                    }

                    <Divider
                        sx={{
                            my: 2,
                        }}
                    />

                    {reserveDetail &&
                        <Paper
                            variant="outlined"
                            sx={{
                                p: 1,
                            }}
                        >
                            <ReserveTotal
                                studioAdjustmentList={reserveDetail?.studioAdjustmentList}
                                equipmentAdjustmentList={reserveDetail?.equipmentAdjustmentList}
                            />
                        </Paper>
                    }


                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            setCancelDialog(true)
                        }}
                        variant="outlined"
                        color="error"
                        disabled={(cancelDialog || reserveDetail?.studioAdjustmentList.some( value => value.is_settled )) ?? false}
                    >予約をキャンセル</Button>
                    <Button
                        onClick={() => {
                            onClose()
                            setDialog(false)
                        }}
                        variant="contained"
                    >閉じる</Button>
                </DialogActions>
            </Dialog>
            <ReserveCancelDialog
                open={cancelDialog}
                onClose={() => {
                    setCancelDialog(false)
                }}
                onCanceled={() => {
                    setCancelDialog(false)
                    setDialog(false)
                    onClose()
                }}
                stdrv_id={stdrv_id}
            />
        </>
    )
}
export default ReserveDetailDialog